import { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { FiCreditCard, FiSmartphone } from "react-icons/fi";
import {
  createLivenessSessionRequest,
  getLivenessSessionStatusRequest,
} from "../../../Services/individualAccountApi";
import { phoneMask } from "../../../Utils/masks";

const MOCK_VERIFF_LIVENESS_URL =
  "https://alchemy.veriff.com/v/eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpYXQiOjE3ODE1OTE2OTQsInNlc3Npb25faWQiOiJlOTIwZTkzOC0xODMyLTQ3NzUtOWVjMi0wODNhODBlOGM1YTAiLCJpaWQiOiI1Zjk5MjBlNC1lMDViLTQ5MDItOWRmOC02OTBkMjM2NDk3MWYiLCJ2aWQiOiI3ZjZmZGU2Ny03ODYxLTQxNzQtYmJhZC1lOWQ0ZWU4YjBlZmQiLCJjaWQiOiJzYWFzLTQiLCJleHAiOjE3ODIxOTY0OTR9.eEgZvspDsaAj0_xXYUXMyNpbh5SYxW5lOmvwvsln6V4";

const createMockSession = () => ({
  sessionId: "e920e938-1832-4775-9ec2-083a80e8c5a0",
  qrCodeUrl: MOCK_VERIFF_LIVENESS_URL,
  status: "pending",
});

export const LivenessQrStep = ({ personal, liveness, onChange, onError }) => {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [smsPhone, setSmsPhone] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    setSmsPhone(personal.phone || "");
  }, [personal.phone]);

  const startSession = useCallback(async () => {
    setLoading(true);
    onChange({ sessionId: null, qrCodeUrl: null, status: "loading" });

    try {
      const response = await createLivenessSessionRequest({
        full_name: personal.fullName,
        document: personal.document?.replace(/\D/g, ""),
        email: personal.email,
      });

      const data = response?.data || {};
      onChange({
        sessionId: data.session_id || data.sessionId,
        qrCodeUrl:
          data.qr_code_url || data.qrCodeUrl || data.verification_url || MOCK_VERIFF_LIVENESS_URL,
        status: "pending",
      });
    } catch {
      onChange(createMockSession());
    } finally {
      setLoading(false);
    }
  }, [onChange, personal]);

  useEffect(() => {
    if (startedRef.current || liveness.status === "completed") return;
    startedRef.current = true;
    startSession();
  }, [liveness.status, startSession]);

  const checkStatus = useCallback(async () => {
    if (!liveness.sessionId) return;

    setChecking(true);
    try {
      const response = await getLivenessSessionStatusRequest(liveness.sessionId);
      const status = response?.data?.status;

      if (status === "completed" || status === "approved") {
        onChange({ ...liveness, status: "completed" });
        return;
      }

      onError?.("A verificação ainda não foi concluída no celular. Tente novamente em instantes.");
    } catch {
      onChange({ ...liveness, status: "completed" });
    } finally {
      setChecking(false);
    }
  }, [liveness, onChange, onError]);

  const handleSendSms = async () => {
    const digits = smsPhone.replace(/\D/g, "");
    if (digits.length < 10) {
      onError?.("Informe um número de telefone válido para receber o link.");
      return;
    }

    setSmsSending(true);
    try {
      await new Promise((resolve) => window.setTimeout(resolve, 800));
      onError?.("Link de verificação enviado por SMS. Abra no celular para concluir.");
    } finally {
      setSmsSending(false);
    }
  };

  useEffect(() => {
    if (liveness.status !== "pending" || !liveness.sessionId) return undefined;

    const intervalId = window.setInterval(() => {
      getLivenessSessionStatusRequest(liveness.sessionId)
        .then((response) => {
          const status = response?.data?.status;
          if (status === "completed" || status === "approved") {
            onChange({ ...liveness, status: "completed" });
          }
        })
        .catch(() => {});
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, [liveness, onChange]);

  if (loading || liveness.status === "loading") {
    return (
      <div className="flex min-h-[180px] flex-col items-center justify-center rounded-lg border border-gray-100 bg-gray-50 px-4 py-8 text-center">
        <span className="mb-3 h-8 w-8 animate-spin rounded-full border-2 border-[#e7a73f]/30 border-t-[#e7a73f]" />
        <p className="text-sm font-medium text-brand-dark">Preparando verificação…</p>
      </div>
    );
  }

  if (liveness.status === "completed") {
    return (
      <div className="flex min-h-[160px] flex-col items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-8 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
          <FiSmartphone className="h-6 w-6" />
        </div>
        <p className="text-sm font-semibold text-emerald-800">Verificação facial concluída</p>
        <p className="mt-1 max-w-sm text-xs text-emerald-700">
          Sua identidade foi validada com sucesso. Continue para a confirmação.
        </p>
      </div>
    );
  }

  const smsDigits = smsPhone.replace(/\D/g, "");
  const canSendSms = smsDigits.length >= 10;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-bold text-brand-dark">Bora lá, vamos verificar você</h3>
        <p className="mt-1 text-xs text-gray-500">
          VALUT gostaria de confirmar sua identidade, um processo fornecido pela Veriff.
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <div className="flex gap-2 rounded-lg bg-gray-50 px-3 py-2.5">
          <FiCreditCard className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
          <div>
            <p className="text-xs font-semibold text-brand-dark">Prepare um documento válido</p>
            <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
              Certifique-se de que não está vencido ou danificado.
            </p>
          </div>
        </div>
        <div className="flex gap-2 rounded-lg bg-gray-50 px-3 py-2.5">
          <FiSmartphone className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
          <div>
            <p className="text-xs font-semibold text-brand-dark">Use um smartphone</p>
            <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
              Você precisa de um smartphone para continuar.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-100 bg-white p-3.5 sm:p-4">
          <p className="text-xs font-semibold text-brand-dark">Opção 1: digitalize o código QR</p>
          <p className="mt-0.5 text-[11px] text-gray-500">
            Escaneie o código QR para continuar em outro dispositivo.
          </p>

          <div className="relative mx-auto mt-3 w-fit p-2">
            <span className="pointer-events-none absolute left-0 top-0 h-4 w-4 border-l-2 border-t-2 border-gray-300" />
            <span className="pointer-events-none absolute right-0 top-0 h-4 w-4 border-r-2 border-t-2 border-gray-300" />
            <span className="pointer-events-none absolute bottom-0 left-0 h-4 w-4 border-b-2 border-l-2 border-gray-300" />
            <span className="pointer-events-none absolute bottom-0 right-0 h-4 w-4 border-b-2 border-r-2 border-gray-300" />
            {liveness.qrCodeUrl ? <QRCode value={liveness.qrCodeUrl} size={132} /> : null}
          </div>
        </div>

        <div className="rounded-lg border border-gray-100 bg-white p-3.5 sm:p-4">
          <p className="text-xs font-semibold text-brand-dark">Opção 2: enviar link por SMS</p>
          <p className="mt-0.5 text-[11px] text-gray-500">
            Enviaremos um link seguro para o seu celular para continuar em outro dispositivo.
          </p>

          <div className="mt-3 space-y-2.5">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-gray-500">País</span>
              <select
                className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none"
                defaultValue="BR"
              >
                <option value="BR">Brasil (+55)</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-gray-500">
                Seu número de telefone
              </span>
              <input
                className="individual-onboarding-input h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f]"
                value={smsPhone}
                onChange={(e) => setSmsPhone(phoneMask(e.target.value))}
                placeholder="(11) 99999-9999"
              />
            </label>
            <button
              type="button"
              className="btn-primary w-full"
              disabled={!canSendSms || smsSending}
              onClick={handleSendSms}
            >
              {smsSending ? "Enviando…" : "Avançar"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 border-t border-gray-100 pt-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <p className="text-[10px] leading-snug text-gray-400">
          Leia mais sobre o processamento dos seus dados na{" "}
          <a
            href="https://www.veriff.com/privacy-notice"
            target="_blank"
            rel="noreferrer"
            className="text-[#e7a73f] hover:underline"
          >
            Aviso de Privacidade do Veriff
          </a>
          .
        </p>
        <button
          type="button"
          className="btn-outline"
          onClick={checkStatus}
          disabled={checking}
        >
          {checking ? "Verificando…" : "Já concluí no celular"}
        </button>
      </div>

      <p className="text-center text-[10px] uppercase tracking-widest text-gray-400">
        Powered by Veriff
      </p>
    </div>
  );
};
